import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message, Spin } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ComptrollerDataTable from 'pages/comptroller/comptroller-table/ComptrollerDataTable';
import IndeterminateCheckbox from 'components/@extended/IndeterminateCheckbox';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetSolutionNameMutation, useIdentityBulkMutation } from 'store/features/process/processApi';
import { groupBy, isEqual } from 'lodash';
import { toastConfig } from 'config';
import controls from 'assets/images/icons/deactivated.svg';
import { activeInactiveStatus } from 'pages/globals/global';

import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Box, useTheme } from '@mui/material';
import { IdentityFeaturedTable } from 'pages/IdentityCase/Table';

// import { useDispatch } from 'react-redux';
const measureText = (text, font = '14px Arial') => {
	// Re-use the same canvas element to be more efficient.
	const canvas = measureText.canvas || (measureText.canvas = document.createElement('canvas'));
	const context = canvas.getContext('2d');
	if (context.font !== font) {
		context.font = font;
	}
	const metrics = context.measureText(String(text)); // Ensure text is a string
	return metrics.width;
};
const MAX_COLUMN_WIDTHS = {
	leaseTitle: 300,
	leaseId: 150,
};
export function formatLeaveType(word) {
	// Handle empty or invalid input to avoid errors
	if (!word || typeof word !== 'string') {
		return 'Invalid Input';
	}
	const capitalized = word.charAt(0).toUpperCase() + word.slice(1);
	return `${capitalized} Leave`;
}
const ListViewLeavePage = ({
	listData = [],
	isLoading,
	selectedTabName = 'All',
	currentRoute = 'v6',
	setData,
	processDetails,
	permissionsObject = null,
	headMetaData = null,
	expectedURLToRedirect = null,
	setRefresh = () => {},
	refetchData = () => {},
}) => {
	const navigate = useNavigate();
	const [submitData, setSubmitData] = useState(null);
	const [rowSelection, setRowSelection] = useState([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [modalData, setModalData] = useState({});
	const [showModal, setShowModal] = useState(false);

	// console.log(selectedTabName, 'selectedTabName123');

	const [
		IdentityBulk,
		{
			data: activationData,
			isSuccess: isCaseSuccess,
			// isLoading: isCaseLoading,
			isError: isCaseError,
		},
	] = useIdentityBulkMutation();

	// const dispatch = useDispatch();
	// Use passed processDetails if available, otherwise use the one from API
	// Get route paths based on version
	// const getRoutePaths = () => {
	// 	const basePath = `/static/lease-record-management-${currentRoute}`;
	// 	return {
	// 		details: `${basePath}/details`,
	// 		edit: `${basePath}/edit`,
	// 		list: `${basePath}/MzEwMQ`, // Default route ID
	// 	};
	// };
	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;

	const extractCleanRowData = (selection) => {
		return selection.map((item) => {
			// Extract only the necessary data, avoiding circular references
			return {
				_id: item.original?._id || item._id,
				id: item.original?.id || item.id,
				activationStatus: item.original?.activationStatus,
				recordStatus: item.original?.recordStatus,
				approverUserId: item.original?.approverUserId,
				// Add other necessary fields without circular references
				data: item.original?.data
					? {
							leaseId: item.original.data.leaseId,
							leaseTitle: item.original.data.leaseTitle,
							// Add other needed data fields
						}
					: item.original,
			};
		});
	};
	const areSelectedKeysEqual = (prev, current) => {
		// Use lodash isEqual for deep comparison instead of JSON.stringify
		return isEqual(prev, current);
	};

	const updateSelectedKeys = useCallback((selection) => {
		if (selection?.length) {
			// Extract clean data to avoid circular references
			const cleanSelection = extractCleanRowData(selection);

			const grouped = groupBy(cleanSelection, ({ activationStatus }) => activationStatus);

			// Use deep comparison instead of JSON.stringify
			setSelectedRowKeys((prev) => {
				if (!areSelectedKeysEqual(prev, grouped)) {
					return grouped;
				}
				return prev; // No update = no re-render
			});
		} else {
			// Handle empty selection
			setSelectedRowKeys((prev) => {
				if (Object.keys(prev).length > 0) {
					return {};
				}
				return prev;
			});
		}
	}, []);

	useEffect(() => {
		updateSelectedKeys(rowSelection);
	}, [rowSelection, updateSelectedKeys]);

	useEffect(() => {
		if (isCaseSuccess) {
			if (activationData?.successCount > 0) {
				toast.success(`Success! ${activationData?.successCount} Action completed`, toastConfig);
			}

			if (activationData?.failedCount > 0) {
				toast.error(`Failed! ${activationData?.failedCount} Action incomplete`, toastConfig);
			}

			// Refetch data after successful operations for proper sync
			setRefresh((prev) => !prev);
			refetchData();
		} else if (isCaseError) {
			toast.error(`${processDetails?.name} status updating error!`, toastConfig);
		}

		setSelectedRowKeys([]);
		setRowSelection({});
		setSubmitData(null);
	}, [isCaseError, isCaseSuccess, setRefresh, refetchData]);

	// const routes = getRoutePaths();

	// Clear selection when tab changes
	useEffect(() => {
		setRowSelection([]);
	}, [selectedTabName]);

	// TanStack Table column definitions for IdentityFeaturedTable
	const TANSTACK_COLUMNS = useMemo(
		() => {
			const baseTanstackColumns = [
				{
					header: 'Leave Type',
					// accessorKey: 'label',
					accessorFn: (row) => {
						const leaveTypeData = row?.label || row?.leaveType || 'N/A';
						return leaveTypeData;
					},
					dataType: 'text',
					enableColumnFilter: true,
					meta: {
						style: { textAlign: 'left' },
					},
				},
				{
					header: 'Policy Category',
					// accessorKey: 'categoryLabel',
					accessorFn: (row) => {
						const policyCategory = row?.categoryLabel || row?.policyCategory || 'N/A';
						return policyCategory;
					},
					dataType: 'text',
					enableColumnFilter: true,
					meta: {
						style: { textAlign: 'left' },
					},
				},
				{
					header: 'Effective Date',
					accessorKey: 'effectiveDateTime',
					accessorFn: (row) =>
						dayjs(row.data?.effectiveDateTime).format('YYYY-MM-DD') || dayjs(row.effectiveDateTime).format('YYYY-MM-DD') || 'N/A',
					dataType: 'text',
					enableColumnFilter: true,
					meta: {
						style: { textAlign: 'left' },
					},
				},
			];

			// Conditionally add the selection column
			if (selectedTabName === 'Worklist') {
				const selectionColumn = {
					id: 'Selection',
					header: ({ table }) => (
						<IndeterminateCheckbox
							{...{
								checked: table.getIsAllRowsSelected(),
								indeterminate: table.getIsSomeRowsSelected(),
								onChange: table.getToggleAllRowsSelectedHandler(),
								sx: { color: 'white', padding: '0px' },
							}}
						/>
					),
					cell: ({ row }) => (
						<IndeterminateCheckbox
							{...{
								checked: row.getIsSelected(),
								disabled: !row.getCanSelect(),
								indeterminate: row.getIsSomeSelected(),
								onChange: row.getToggleSelectedHandler(),
							}}
						/>
					),
					meta: {
						style: { textAlign: 'center', width: '57px' },
					},
					enableSorting: false,
					enableColumnFilter: false,
				};

				const statusColumn = {
					header: 'Status',
					accessorKey: 'activationStatus',
					accessorFn: (row) => row.activationStatus || 'N/A',
					cell: ({ getValue }) => activeInactiveStatus(getValue()),
					dataType: 'text',
					enableColumnFilter: true,
				};
				return [selectionColumn, ...baseTanstackColumns, statusColumn];
			}

			// Return only base columns for other tabs
			return baseTanstackColumns;
		},
		[selectedTabName] // Add selectedTabName to the dependency array
	);

	// Handle row click - similar to navigateToDetails in IdentityCaseList
	// const navigateToDetails = (row) => {
	// 	navigate(routes.details, {
	// 		state: {
	// 			leaseDetails: row,
	// 			listData,
	// 			selectedTab: selectedTabName,
	// 			_id: row._id || row.id,
	// 			processCode: '10101',
	// 			processName: 'Lease Records',
	// 		},
	// 	});
	// };

	// Handle row selection for IdentityFeaturedTable
	// const handleSelectRow = (row) => {
	// 	// console.log(row,'row2233');
	// 	if (headMetaData?.meta?.staticFormUrl?.startsWith('/static/') || permissionsObject?.write === true) {
	// 		const url = headMetaData?.meta?.staticFormUrl ?? '/case-identity';
	// 		const staticURL = headMetaData?.meta?.staticFormUrl;

	// 		const leaveData = {
	// 			category: row?.data?.policyCategory,
	// 			categoryLabel: formatLeaveType(row?.data?.policyCategory),
	// 			key: row?.data?.leaveType,
	// 			label: formatLeaveType(row?.data?.leaveType),
	// 		};
	// 		if (staticURL) {
	// 			navigate(`${url}/details`, {
	// 				state: {
	// 					processCode: processDetails?.processCode,
	// 					leaveCategory: formatLeaveType(row?.data?.policyCategory),
	// 					leaveData,
	// 					leaveKey: row?.data?.leaveType,
	// 					leaveType: formatLeaveType(row?.data?.leaveType),
	// 				},
	// 			});
	// 		} else {
	// 			navigateToDetails(row);
	// 		}
	// 	} else {
	// 		navigateToDetails(row);
	// 	}
	// };

	const navigateToDetails = (row) => {
		if (selectedTabName === 'Active') {
			navigate('/static/leave-combine/policy', {
				state: {
					processCode: processDetails?.processCode || '10127',
					leaveType: row?.leaveType,
					leaveCategory: row?.categoryLabel,
					leaveData: row,
					selectedTab: selectedTabName,
				},
			});
		} else {
			navigate('/static/leave-combine/details', {
				state: {
					// leaseDetails: row,
					selectedTab: selectedTabName,
					_id: row._id || row.id,
					processCode: processDetails?.processCode || '10127',
					processName: processDetails?.name,
				},
			});
		}
	};

	// Approve/Reject handler for IdentityFeaturedTable
	const approveRejectHandler = (data, status) => {
		console.log(data, 'status123');

		const arr = [];
		const ids = [];

		if (Array.isArray(data)) {
			data?.forEach((item) => {
				if (item?.recordStatus === 'parked') {
					ids.push(item.id || item._id);
				} else {
					arr.push(item);
				}
			});
		} else {
			if (data?.recordStatus === 'parked') {
				ids.push(data);
			} else {
				arr.push(data);
			}
		}

		console.log(arr, ids, 'array');

		if (arr?.length > 0) {
			message.error({
				content: 'Your request is inconsistent with selection.',
				icon: <img src={controls} alt="inactive" width="16px" height="16px" style={{ marginRight: '8px', verticalAlign: 'sub' }} />,
			});
		} else {
			setModalData({
				head: processDetails?.name,
				action: status,
				label:
					ids?.length > 1 ? (
						<p>
							Do you want to <b style={{ fontSize: '1rem' }}>{status}</b> all?
						</p>
					) : (
						<p>
							Do you want to <b style={{ fontSize: '1rem' }}>{status}</b>
							{!status?.includes('?') ? '?' : ''}
						</p>
					),
			});

			setSubmitData(ids);
			setShowModal(true);
		}
	};

	const activeDeactivateHandle = (status) => {
		if (status === 'submit') {
			// 1. Get only the selected items that are actually drafts.
			const draftItems = selectedRowKeys['draft'] || [];
			// console.log(draftItems,"draftItems123");

			// 2. Check if any drafts were selected at all.
			if (draftItems.length === 0) {
				message.error({
					content: 'No draft items selected for submission.',
					icon: <img src={controls} alt="inactive" width="16px" height="16px" style={{ marginRight: '8px', verticalAlign: 'sub' }} />,
				});
				return; // Exit function
			}

			// 3. Ensure that all selected items are drafts.
			if (draftItems.length !== rowSelection.length) {
				message.error({
					content: 'Your request is inconsistent. Please select only draft items to submit.',
					icon: <img src={controls} alt="inactive" width="16px" height="16px" style={{ marginRight: '8px', verticalAlign: 'sub' }} />,
				});
				return; // Exit function
			}

			// 4. If all checks pass, prepare data and show the modal.
			const idsToSubmit = draftItems.map((item) => item._id);

			setModalData({
				head: processDetails?.name,
				action: 'Save', // Triggers the correct 'Save' block in modalSubmitHandler
				label: (
					<p>
						Do you want to <b style={{ fontSize: '1rem' }}>submit</b> all selected drafts?
					</p>
				),
			});

			setSubmitData(idsToSubmit);
			setShowModal(true);
			return; // IMPORTANT: Exit the function so the generic validation below is not run.
		}
		// --- END: DEDICATED LOGIC FOR SUBMIT ACTION ---

		// The rest of the function for 'Active'/'Inactive' remains untouched and works as before.
		let arr = [];
		const recordStatusArr = [];

		if (status === 'Active') {
			setModalData({
				head: processDetails?.name,
				action: 'Inactive',
				label:
					rowSelection?.length > 1 ? (
						<p>
							Do you want to <b style={{ fontSize: '1rem' }}>deactivate</b> all?
						</p>
					) : (
						<p>
							Do you want to <b style={{ fontSize: '1rem' }}>deactivate</b> this {processDetails?.name?.toLowerCase()}?
						</p>
					),
			});

			if (selectedRowKeys[status]) {
				selectedRowKeys[status]?.forEach((item) => {
					arr.push({ _id: item?._id });
					if (item?.recordStatus === 'parked') {
						recordStatusArr.push(item);
					}
				});
			}
		} else if (status === 'Inactive') {
			setModalData({
				head: processDetails?.name,
				action: 'Active',
				label:
					rowSelection?.length > 1 ? (
						<p>
							Do you want to <b style={{ fontSize: '1rem' }}>active</b> all?
						</p>
					) : (
						<p>
							Do you want to <b style={{ fontSize: '1rem' }}>active</b> this {processDetails?.name?.toLowerCase()}?
						</p>
					),
			});

			if (selectedRowKeys[status]) {
				selectedRowKeys[status]?.forEach((item) => {
					arr.push({ _id: item?._id });
					if (item?.recordStatus === 'parked') {
						recordStatusArr.push(item);
					}
				});
			} else if (selectedRowKeys['W-I-P']) {
				selectedRowKeys['W-I-P']?.forEach((item) => {
					arr.push({ _id: item?._id });
					if (item?.recordStatus === 'parked') {
						recordStatusArr.push(item);
					}
				});
			} else if (selectedRowKeys['null']) {
				selectedRowKeys['null']?.forEach((item) => {
					arr.push({ _id: item?._id });
					if (item?.recordStatus === 'parked') {
						recordStatusArr.push(item);
					}
				});
			}
		}

		// This generic validation now only runs for 'Active' and 'Inactive' actions.
		if (arr.length !== rowSelection.length || recordStatusArr.length > 0) {
			message.error({
				content: 'Your request is inconsistent with selection.',
				icon: <img src={controls} alt="inactive" width="16px" height="16px" style={{ marginRight: '8px', verticalAlign: 'sub' }} />,
			});
		} else {
			setSubmitData(arr);
			setShowModal(true);
		}
	};

	const modalSubmitHandler = () => {
		if (modalData?.action === 'Active') {
			const payload = {
				_id: submitData.map((item) => item._id),
				operation: 'activate-bulk',
				processCode: processDetails?.processCode,
				activationStatus: 'Active',
			};

			IdentityBulk(payload);
		} else if (modalData?.action === 'Inactive') {
			const payload = {
				_id: submitData.map((item) => item._id),
				operation: 'activate-bulk',
				processCode: processDetails?.processCode,
				activationStatus: 'Inactive',
			};

			IdentityBulk(payload);
		} else if (modalData?.action === 'Approve') {
			IdentityBulk({
				_id: submitData,
				operation: 'approve-bulk',
				processCode: processDetails.processCode,
				suffixCode: '',
			});
		} else if (modalData?.action === 'Reject') {
			IdentityBulk({
				_id: submitData,
				operation: 'reject-bulk',
				processCode: processDetails.processCode,
			});
		} else if (modalData?.action === 'Save') {
			// This is triggered by the 'Submit' button

			const payload = {
				_id: submitData,
				operation: 'submit-draft-bulk',
				processCode: processDetails.processCode,
				suffixCode: '',
			};

			IdentityBulk(payload);
		}
	};

	const buttonVisibility = useMemo(
		() => ({
			submit: selectedTabName === 'Draft',
			deactivate: selectedTabName === 'Active',
			activate: selectedTabName === 'Inactive',
			approveReject: !['Active', 'NEW_INACTIVE', 'Inactive', 'Draft', 'All', 'W-I-P'].includes(selectedTabName),
		}),
		[selectedTabName]
	);
	// console.log(selectedTabName, 'selectedTabName123');

	return (
		<Spin spinning={isLoading}>
			<IdentityFeaturedTable
				columns={TANSTACK_COLUMNS}
				setData={setData}
				rowSelection={rowSelection}
				processDetails={processDetails}
				xlsxFileName={processDetails?.name || 'lease-records'}
				setRowSelection={setRowSelection}
				data={listData ?? []}
				approveRejectHandler={approveRejectHandler}
				activeDeactivateHandle={activeDeactivateHandle}
				selectedTabName={selectedTabName}
				expectedURLToRedirect={expectedURLToRedirect}
				handleSelectRow={navigateToDetails}
				buttonVisibility={buttonVisibility}
			/>

			{showModal && (
				<ComptrollerConfirmModal showModal={showModal} setShowModal={setShowModal} modalData={modalData} handleSubmit={modalSubmitHandler} />
			)}
		</Spin>
	);
};

export default ListViewLeavePage;
